import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { GoogleSignInButton } from "@/components/GoogleSignInButton";
import {
  AUTH_TOKEN_KEY, fetchMe, loginAccount, classroomRegister, forgotPassword,
  cCreateCourse, cListCourses, cCourseDetail, cCreateAssignment, cInviteStudents, cRemoveStudents, cUpdateCourse, cAssignmentLibrary, cCopyAssignment, cUpdateAssignment, cArchiveAssignment, cDeleteAssignment, cStudentArchiveAssignment, dueLabel, isPastDue,
  cAssignmentSubmissions, cSubmissionDetail, cSaveFeedback,
  cJoinCourse, cMyCourses, cCourseAssignments, cStartAssignment, cMySubmission,
} from "@/lib/api";
import { Eye, EyeOff, ArrowLeft, MailCheck, PenLine } from "lucide-react";
import { useOnboarding } from "@/onboarding/context";

// WALKTHROUGH LINK — replays a replica walkthrough (teacher dashboard · new student start); offers itself once on first visit
function WalkthroughLink({ tour, label, testid }) {
  const onboarding = useOnboarding();
  useEffect(() => { onboarding?.offerReplica?.(tour); }, [onboarding, tour]);
  if (!onboarding) return null;
  return <button type="button" data-testid={testid} onClick={() => onboarding.startReplica(tour)} className="text-[12.5px] text-stone-500 underline decoration-stone-300 underline-offset-4 hover:text-[#8C3A2A]">{label}</button>;
}
import { CourseDevelopment, StudentDevelopment, AssignmentDevelopment } from "@/components/DevelopmentDashboard";
import MyDevelopment from "@/components/MyDevelopment";
import CourseRoster from "@/components/CourseRoster";
import { AdminPanel, AdminUserDetail } from "@/components/AdminConsole";
import StrongScaffoldingPanel from "@/components/StrongScaffoldingPanel";
import DevelopmentalRangePanel from "@/components/DevelopmentalRangePanel";
import CourseWritingProfile from "@/components/CourseWritingProfile";
import AssignmentWritingProfile from "@/components/AssignmentWritingProfile";
import { StudentInstructorAssessment, TeacherAssessmentPanel, CourseGradebook } from "@/components/InstructorAssessment";

const BRAND = "#8C3A2A";
const btn = "px-4 py-2 rounded-sm text-sm font-medium transition-colors disabled:opacity-40";
const primary = `${btn} bg-[#8C3A2A] text-white hover:bg-[#6B2C20]`;

// Increment 7 — compact assignment context for TWS (no rubric engine, no source management).
const EMPTY_ASG = { title: "", prompt: "", instructions: "", due_date: "", writing_path: "", essay_structure: "", requires_assigned_material: false, assigned_material_note: "", assignment_requirements: [], requirements_other: "", teacher_guidance: "" };
const REQUIREMENT_OPTIONS = [["evidence", "Use evidence"], ["assigned_sources", "Use assigned sources"], ["compare_contrast", "Compare or contrast"], ["causes", "Explain causes or mechanisms"],
  ["counterargument", "Address a counterargument or alternative view"], ["implications", "Discuss implications/significance"], ["other", "Other"]];

function AssignmentContextFields({ asg, onChange }) {
  const field = "w-full border border-stone-300 rounded-sm px-3 py-2 text-sm bg-white";
  const label = "block text-[12px] text-stone-600 mb-1";
  const toggle = (key) => onChange({ ...asg, assignment_requirements: asg.assignment_requirements.includes(key) ? asg.assignment_requirements.filter((k) => k !== key) : [...asg.assignment_requirements, key] });
  return (
    <div data-testid="asg-context" className="space-y-3 mb-3">
      <div>
        <label className={label} htmlFor="asg-requires-material">Should students use assigned material before developing their main point?</label>
        <select id="asg-requires-material" data-testid="asg-requires-material" className={field} value={asg.requires_assigned_material ? "yes" : "no"}
          onChange={(e) => onChange({ ...asg, requires_assigned_material: e.target.value === "yes" })}>
          <option value="no">No</option>
          <option value="yes">Yes</option>
        </select>
        {asg.requires_assigned_material && (
          <input data-testid="asg-material-note" className={`${field} mt-2`} maxLength={600} placeholder="What should students read, review, or use? e.g. Chapters 3 and 4"
            value={asg.assigned_material_note} onChange={(e) => onChange({ ...asg, assigned_material_note: e.target.value })} />
        )}
      </div>
      <div>
        <p className={label}>What does this assignment require?</p>
        <div className="grid grid-cols-1 gap-1">
          {REQUIREMENT_OPTIONS.map(([key, text]) => (
            <label key={key} className="flex items-center gap-2 text-[13px] text-stone-800">
              <input type="checkbox" data-testid={`asg-req-${key}`} checked={asg.assignment_requirements.includes(key)} onChange={() => toggle(key)} className="accent-[#8C3A2A]" /> {text}
            </label>
          ))}
        </div>
        {asg.assignment_requirements.includes("other") && (
          <input data-testid="asg-req-other-text" className={`${field} mt-2`} maxLength={300} placeholder="Other requirement, in a few words"
            value={asg.requirements_other} onChange={(e) => onChange({ ...asg, requirements_other: e.target.value })} />
        )}
        <p className="text-[11px] text-stone-500 mt-1">Requirements the argument must satisfy somewhere — TWS helps each student work out where. They do not become essay sections.</p>
      </div>
      <div>
        <label className={label} htmlFor="asg-guidance">Anything else TWS should keep in mind while guiding students? <span className="text-stone-400">(optional, not shown to students)</span></label>
        <textarea id="asg-guidance" data-testid="asg-guidance" className={`${field} h-16`} maxLength={1000} placeholder="e.g. Students should compare the two authors rather than summarize them separately."
          value={asg.teacher_guidance} onChange={(e) => onChange({ ...asg, teacher_guidance: e.target.value })} />
      </div>
    </div>
  );
}
const ghost = `${btn} border border-stone-300 text-stone-700 hover:border-[#8C3A2A] hover:text-[#8C3A2A]`;

// Two different doors, explained where the teacher chooses: Full Studio routes develop the thinking and the writing; Strengthen revises writing that exists.
const PATH_NOTES = {
  draft_first: "Full Writing Studio. Students who already have an early draft rethink and develop the paper from it: Existing writing → Understand → Thesis → Build → Organize → Write → Strengthen → Finish.",
  strengthen_existing: "Strengthen My Writing. For writing that is substantially developed: students bring in a paragraph, a response or a full paper and revise it — Existing writing → Understand its structure → Strengthen → Finish. No thesis, ideas, build or organize unless they choose to move into Full Studio.",
  thesis_first: "Full Writing Studio: Thesis → Ideas → Build → Organize → Write → Strengthen → Finish.",
  ideas_first: "Full Writing Studio: Ideas → Thesis → Build → Organize → Write → Strengthen → Finish.",
  student_choice: "Full Writing Studio; each student picks thesis-first, ideas-first or an early draft once.",
};
const PathNote = ({ path }) => PATH_NOTES[path] ? <p data-testid={`path-note-${path}`} className="text-[11.5px] text-stone-500 -mt-2 mb-3 leading-snug">{PATH_NOTES[path]}</p> : null;

// One set of assignment fields for BOTH creating and editing — every parameter is always open to the teacher.
function AssignmentFields({ asg, onChange, prefix }) {
  const set = (patch) => onChange({ ...asg, ...patch });
  const id = (k) => `${prefix}asg-${k}`;
  return (
    <>
      <input data-testid={id("title")} className="w-full border border-stone-300 rounded-sm px-3 py-2 text-sm mb-2" placeholder="Assignment title" value={asg.title || ""} onChange={(e) => set({ title: e.target.value })} />
      <textarea data-testid={id("prompt")} className="w-full border border-stone-300 rounded-sm px-3 py-2 text-sm mb-2 h-20" placeholder="Prompt" value={asg.prompt || ""} onChange={(e) => set({ prompt: e.target.value })} />
      <label className="block text-[12px] text-stone-600 mb-1" htmlFor={id("due")}>Due date (optional)</label>
      <input id={id("due")} data-testid={id("due")} type="date" className="border border-stone-300 rounded-sm px-3 py-2 text-sm mb-3 bg-white" value={asg.due_date || ""} onChange={(e) => set({ due_date: e.target.value })} />
      <label className="block text-[12px] text-stone-600 mb-1" htmlFor={id("writing-path")}>How should students begin?</label>
      <select id={id("writing-path")} data-testid={id("writing-path")} className="w-full border border-stone-300 rounded-sm px-3 py-2 text-sm mb-3 bg-white" value={asg.writing_path || ""} onChange={(e) => set({ writing_path: e.target.value })}>
        <option value="">Current Writing Studio experience</option>
        <option value="thesis_first">Start with a thesis</option>
        <option value="ideas_first">Explore ideas before forming a thesis</option>
        <option value="draft_first">Start from existing writing</option>
        <option value="student_choice">Let students choose</option>
        <option value="strengthen_existing">Strengthen existing writing</option>
      </select>
      <PathNote path={asg.writing_path || ""} />
      <label className="block text-[12px] text-stone-600 mb-1" htmlFor={id("essay-structure")}>What structure should students use?</label>
      <select id={id("essay-structure")} data-testid={id("essay-structure")} className="w-full border border-stone-300 rounded-sm px-3 py-2 text-sm mb-3 bg-white" value={asg.essay_structure || ""} onChange={(e) => set({ essay_structure: e.target.value })}>
        <option value="">Flexible</option>
        <option value="intro_body_conclusion">Introduction / body / conclusion</option>
        <option value="five_paragraph">Five-paragraph essay</option>
      </select>
      <AssignmentContextFields asg={asg} onChange={onChange} />
    </>
  );
}

const STATUS_LABEL = { not_started: "Not started", in_progress: "In progress", complete: "Completed" };
// LATE FLAG — the most recent submitted version arrived after the assignment's due date.
const LateFlag = ({ testid }) => (
  <span data-testid={testid} title="Submitted after the due date" className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-[#8C3A2A] text-white">Late</span>
);
const StatusPill = ({ s }) => (
  <span data-testid={`status-${s}`} className={
    "text-[11px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full " +
    (s === "complete" ? "bg-emerald-50 text-emerald-700"
      : s === "in_progress" ? "bg-amber-50 text-amber-700" : "bg-stone-100 text-stone-500")
  }>{STATUS_LABEL[s] || s}</span>
);

function Auth({ onAuthed }) {
  const [mode, setMode] = useState("login"); // "login" | "register" | "forgot"
  const [role, setRole] = useState("student");
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [showPw, setShowPw] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const res = mode === "login"
        ? await loginAccount({ email: form.email, password: form.password })
        : await classroomRegister({ ...form, role });
      localStorage.setItem(AUTH_TOKEN_KEY, res.token);
      onAuthed(res.user);
    } catch (err) {
      const d = err?.response?.data?.detail;
      let msg = mode === "login" ? "Could not sign in" : "Could not create account";
      if (typeof d === "string") msg = d;
      else if (Array.isArray(d) && d.length) msg = d[0]?.msg || msg; // FastAPI 422 validation list
      toast.error(msg);
    } finally { setBusy(false); }
  };
  const submitForgot = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await forgotPassword(form.email);
      setForgotSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send the reset email. Please try again.");
    } finally { setBusy(false); }
  };

  if (mode === "forgot") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50 px-4">
        <div data-testid="classroom-forgot" className="w-full max-w-sm bg-white border border-stone-200 rounded-md p-8">
          <p className="font-mono text-[11px] uppercase tracking-[0.2em]" style={{ color: BRAND }}>The Writing Studio Classroom</p>
          {forgotSent ? (
            <div className="text-center py-3" data-testid="classroom-forgot-sent">
              <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-emerald-50 ring-1 ring-emerald-100">
                <MailCheck className="h-5 w-5 text-emerald-600" />
              </div>
              <p className="text-[14px] text-stone-700 mb-1">Check your inbox</p>
              <p className="text-[13px] text-stone-500 mb-5">
                If an account exists for <span className="text-stone-700">{form.email}</span>, a reset link is on its way (check spam too).
              </p>
              <button type="button" data-testid="classroom-forgot-back"
                onClick={() => { setMode("login"); setForgotSent(false); }}
                className="inline-flex items-center gap-1.5 text-[12.5px] text-[#8C3A2A] hover:underline">
                <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
              </button>
            </div>
          ) : (
            <form onSubmit={submitForgot}>
              <h1 className="mt-2 font-serif text-3xl text-stone-900">Reset your password</h1>
              <p className="mt-2 text-[13px] text-stone-500">Enter your email and we'll send you a link to set a new password.</p>
              <input data-testid="classroom-forgot-email" className="mt-4 w-full border border-stone-300 rounded-sm px-3 py-2 text-sm"
                placeholder="Email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              <button data-testid="classroom-forgot-submit" disabled={busy} className={primary + " mt-5 w-full"}>Send reset link</button>
              <button type="button" onClick={() => setMode("login")} data-testid="classroom-forgot-cancel"
                className="mt-3 w-full inline-flex items-center justify-center gap-1.5 text-[13px] text-stone-500 hover:text-[#8C3A2A]">
                <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
              </button>
            </form>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-stone-50 px-4">
      <form onSubmit={submit} data-testid="classroom-auth" className="w-full max-w-sm bg-white border border-stone-200 rounded-md p-8">
        <p className="font-mono text-[11px] uppercase tracking-[0.2em]" style={{ color: BRAND }}>The Writing Studio Classroom</p>
        <h1 className="mt-2 font-serif text-3xl text-stone-900">{mode === "login" ? "Sign in" : "Create account"}</h1>
        {mode === "register" && (
          <div className="mt-5 flex gap-2" data-testid="role-toggle">
            {[["student", "Student"], ["user", "Individual"]].map(([r, label]) => (
              <button key={r} type="button" onClick={() => setRole(r)} data-testid={`role-${r}`}
                className={(role === r ? primary : ghost) + " flex-1 text-[13px] px-2"}>{label}</button>
            ))}
          </div>
        )}
        {mode === "register" && (
          <p data-testid="teacher-invite-note" className="mt-2 text-[11.5px] text-stone-400 leading-snug">
            Teacher accounts are set up by invitation — ask your administrator.
          </p>
        )}
        {mode === "register" && (
          <input data-testid="auth-name" className="mt-3 w-full border border-stone-300 rounded-sm px-3 py-2 text-sm"
            placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        )}
        <input data-testid="auth-email" className="mt-3 w-full border border-stone-300 rounded-sm px-3 py-2 text-sm"
          placeholder="Email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        <div className="relative mt-3">
          <input data-testid="auth-password" className="w-full border border-stone-300 rounded-sm px-3 py-2 pr-10 text-sm"
            placeholder="Password" type={showPw ? "text" : "password"} required minLength={10} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          <button type="button" onClick={() => setShowPw((s) => !s)} data-testid="auth-password-toggle"
            aria-label={showPw ? "Hide password" : "Show password"}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-stone-400 hover:text-[#8C3A2A] transition-colors">
            {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
        {mode === "register" && <p className="mt-1 text-[12px] text-stone-400" data-testid="auth-password-hint">At least 10 characters.</p>}
        {mode === "login" && (
          <button type="button" onClick={() => setMode("forgot")} data-testid="classroom-forgot-link"
            className="mt-2 text-[12.5px] text-[#8C3A2A] hover:underline">Forgot password?</button>
        )}
        <button data-testid="auth-submit" disabled={busy} className={primary + " mt-5 w-full"}>{mode === "login" ? "Sign in" : "Create account"}</button>
        <GoogleSignInButton asRole={role === "student" ? "student" : ""} onError={(m) => toast.error(m)}
          onAuthed={(data) => { localStorage.setItem(AUTH_TOKEN_KEY, data.token); onAuthed(data.user); }} />
        <button type="button" onClick={() => setMode(mode === "login" ? "register" : "login")}
          data-testid="auth-switch" className="mt-3 w-full text-[13px] text-stone-500 hover:text-[#8C3A2A]">
          {mode === "login" ? "Need an account? Register" : "Have an account? Sign in"}
        </button>
      </form>
    </div>
  );
}

// TOP NAVIGATION for educators — the whole portal in one row: Courses · Admin (administrators only) · Student view. Plain paths, no suffixes.
const NavLink = ({ testid, active, onClick, href, children }) => {
  const cls = `text-[13px] px-2.5 py-1 rounded-full transition-colors ${active ? "bg-[#8C3A2A] text-white" : "text-stone-600 hover:text-[#8C3A2A] hover:bg-stone-100"}`;
  return href ? <a data-testid={testid} href={href} className={cls}>{children}</a> : <button data-testid={testid} onClick={onClick} className={cls} aria-current={active ? "page" : undefined}>{children}</button>;
};
// An administrator's portal is the Admin console; "My courses" appears only if that administrator actually owns courses (created as a teacher would).
const Shell = ({ user, onSignOut, children, crumbs, onCrumb, section, onSection, ownsCourses = true }) => (
  <div className="min-h-screen bg-stone-50">
    <header className="border-b border-stone-200 bg-white">
      {onSection ? (
        <nav data-testid="educator-nav" aria-label="Educator portal" className="max-w-5xl mx-auto px-6 pt-3 flex items-center gap-1 flex-wrap">
          {user.role === "admin" ? <NavLink testid="nav-admin" active={section === "admin"} onClick={() => onSection("admin")}>Admin</NavLink> : null}
          {user.role !== "admin" || ownsCourses ? <NavLink testid="nav-courses" active={section === "courses"} onClick={() => onSection("courses")}>{user.role === "admin" ? "My courses" : "Courses"}</NavLink> : null}
          <NavLink testid="nav-student-view" href="/student">Student view</NavLink>
        </nav>
      ) : null}
      <div className="max-w-5xl mx-auto px-6 py-4 flex flex-wrap items-center justify-between gap-y-2">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm min-w-0">
          <a href="/" data-testid="shell-home-link" title="Home" className="flex items-center gap-2 group mr-2">
            <span className="grid place-items-center h-6 w-6 rounded-sm bg-[#8C3A2A]"><PenLine className="h-3.5 w-3.5 text-white" /></span>
            <span className="font-serif text-[15px] text-stone-900 group-hover:text-[#8C3A2A] transition-colors">The Writing Studio</span>
          </a>
          <span className="text-stone-300">|</span>
          {(crumbs || []).map((c, i) => (
            <span key={i} className="flex items-center gap-3">
              {i > 0 && <span className="text-stone-300">/</span>}
              <button onClick={() => onCrumb(i)} data-testid={`crumb-${i}`}
                className={i === crumbs.length - 1 ? "text-stone-900 font-medium" : "text-stone-500 hover:text-[#8C3A2A]"}>{c.label}</button>
            </span>
          ))}
        </div>
        <div className="flex items-center gap-3 text-[13px] text-stone-500">
          <span data-testid="who">{user.name} · {user.role}</span>
          <button onClick={onSignOut} data-testid="sign-out" className="hover:text-[#8C3A2A]">Sign out</button>
        </div>
      </div>
    </header>
    <main className="max-w-6xl mx-auto px-6 py-8">{children}</main>
  </div>
);

// ---------------- TEACHER ----------------
function Teacher({ user, onSignOut }) {
  // nav.admin = { tab, user? } persists while an administrator drills from Admin → a person → their course / a submission, so the crumbs lead back.
  // an administrator lands on Admin (their portal); a teacher on Courses. /admin and /teacher agree for admins.
  const [nav, setNav] = useState(user.role === "admin" ? { view: "admin", admin: { tab: "teachers" } } : { view: "courses", admin: null });
  const [courses, setCourses] = useState([]);
  const [detail, setDetail] = useState(null);
  const [inviteOpen, setInviteOpen] = useState(false);          // "Send code to students by email" — open by default while a course has no students
  const [rosterSel, setRosterSel] = useState(() => new Set());   // BULK: selected students in the roster (remove from course)
  const removeSelected = async () => {
    const ids = [...rosterSel];
    if (!ids.length || !detail) return;
    if (!window.confirm(`Remove ${ids.length} student${ids.length === 1 ? "" : "s"} from ${detail.course.title}?\n\nTheir accounts and submitted work are kept; they can re-join with the code.`)) return;
    try { const r = await cRemoveStudents(detail.course.id, ids); toast.success(`Removed ${r.removed} from the course`); setRosterSel(new Set()); await openCourse(detail.course.id); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not remove students"); }
  };
  const [subs, setSubs] = useState(null);
  const [sub, setSub] = useState(null);
  const [newCourse, setNewCourse] = useState({ title: "", description: "" });
  const [newAsg, setNewAsg] = useState(EMPTY_ASG);
  const [renaming, setRenaming] = useState(null);        // course rename: the draft title, or null
  const [library, setLibrary] = useState(null);          // every assignment across this teacher's courses (reuse without retyping)
  const [reuse, setReuse] = useState({ source: "", due_date: "" });
  const [reassign, setReassign] = useState(null);        // { a, courseId, due_date } — place an assignment in a course again
  const [editAsg, setEditAsg] = useState(null);          // { id, title, prompt, due_date } — edit an assignment (due date etc.)
  const renameCourse = async () => {
    const t = (renaming || "").trim();
    if (!t || !detail) return;
    try { await cUpdateCourse(detail.course.id, { title: t }); setRenaming(null); toast.success("Course renamed"); await openCourse(detail.course.id); loadCourses(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not rename the course"); }
  };
  const loadLibrary = async () => { try { setLibrary((await cAssignmentLibrary()).assignments || []); } catch { setLibrary([]); } };
  const doReassign = async () => {
    if (!reassign?.a || !reassign.courseId) return;
    try { await cCopyAssignment(reassign.courseId, { source_assignment_id: reassign.a.id, due_date: reassign.due_date }); toast.success(`Assigned to ${courses.find((c) => c.id === reassign.courseId)?.title || "the course"}`); setReassign(null); if (reassign.courseId === detail?.course.id) await openCourse(detail.course.id); loadCourses(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not reassign"); }
  };
  const saveAsg = async () => {
    if (!editAsg) return;
    try {
      const { title, prompt, instructions, due_date, writing_path, essay_structure, requires_assigned_material, assigned_material_note, assignment_requirements, requirements_other, teacher_guidance } = editAsg;
      await cUpdateAssignment(editAsg.id, { title, prompt, instructions: instructions || "", due_date: due_date || "", writing_path: writing_path || "", essay_structure: essay_structure || "", requires_assigned_material: !!requires_assigned_material, assigned_material_note: assigned_material_note || "", assignment_requirements: assignment_requirements || [], requirements_other: requirements_other || "", teacher_guidance: teacher_guidance || "" });
      toast.success("Assignment updated"); setEditAsg(null); await openCourse(detail.course.id);
    }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not update the assignment"); }
  };
  const archiveAsg = async (a, archived) => {
    if (archived && !window.confirm(`Archive “${a.title}”?\n\nStudents will no longer see it. All their work is kept, and you can restore it any time.`)) return;
    try { await cArchiveAssignment(a.id, archived); toast.success(archived ? "Assignment archived" : "Assignment restored"); setEditAsg(null); setReassign(null); await openCourse(detail.course.id); loadCourses(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not update the assignment"); }
  };
  const [deleting, setDeleting] = useState(null);   // { a, typed }
  const deleteAsg = async () => {
    if (!deleting) return;
    try { await cDeleteAssignment(deleting.a.id, deleting.typed); toast.success(`“${deleting.a.title}” deleted`); setDeleting(null); setEditAsg(null); setReassign(null); await openCourse(detail.course.id); loadCourses(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not delete the assignment"); }
  };
  const liveAsgs = (detail?.assignments || []).filter((a) => !a.archived);
  const archivedAsgs = (detail?.assignments || []).filter((a) => a.archived);
  const [comment, setComment] = useState("");
  const [openIter, setOpenIter] = useState(false);
  const [devStudent, setDevStudent] = useState(null);   // {id, name} for per-student development
  const [devSession, setDevSession] = useState(null);   // {id, title} for within-assignment development
  const [workGrid, setWorkGrid] = useState(null);       // {`${assignmentId}:${studentId}`: {session_id, status}}

  const loadCourses = useCallback(async () => {
    try {
      setCourses(await cListCourses());
    } catch (e) {
      if (e?.response?.status === 403 || e?.response?.status === 401) {
        toast.error("This account can't access the teacher dashboard.");
        onSignOut();
      } else {
        toast.error("Could not load courses");
      }
    }
  }, [onSignOut]);
  useEffect(() => { loadCourses(); }, [loadCourses]);

  const openCourse = async (id) => {
    const d = await cCourseDetail(id);
    d.assignments = (d.assignments || []).slice().sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
    setInviteOpen(!(d.students || []).length);
    setRosterSel(new Set());
    setDetail(d); setWorkGrid(null); setNav((n) => ({ view: "course", courseId: id, admin: n.admin }));
    // Student Work grid: session_id + status per (assignment, student), from existing endpoints only.
    try {
      const results = await Promise.all((d.assignments || []).map((a) => cAssignmentSubmissions(a.id).then((r) => ({ a, r }))));
      const map = {};
      results.forEach(({ a, r }) => (r.submissions || []).forEach((row) => {
        map[`${a.id}:${row.student.id}`] = { session_id: row.session_id, status: row.status, late: !!row.late };
      }));
      setWorkGrid(map);
    } catch (e) { setWorkGrid({}); }
  };
  const openAssignment = async (a) => { setSubs(await cAssignmentSubmissions(a.id)); setNav({ view: "assignment", courseId: nav.courseId, a, admin: nav.admin }); };
  const openAdmin = (tab) => setNav((n) => ({ view: "admin", admin: { tab: tab || n.admin?.tab || "teachers" } }));
  const openAdminUser = (u) => setNav((n) => ({ view: "admin-user", admin: { tab: n.admin?.tab || (u.role === "teacher" ? "teachers" : "students"), user: u } }));
  const openAdminSubmission = async (row) => { setSubs(null); setDetail(null); const d = await cSubmissionDetail(row.session_id); setSub(d); setComment(d.feedback?.comment || ""); setOpenIter(false); setNav((n) => ({ view: "submission", sid: row.session_id, courseId: row.course?.id, admin: n.admin })); };
  const openSub = async (sid) => { const d = await cSubmissionDetail(sid); setSub(d); setComment(d.feedback?.comment || ""); setOpenIter(false); setNav({ ...nav, view: "submission", sid }); };

  const adminCrumbs = nav.admin ? [{ label: "Admin" }].concat(nav.admin.user ? [{ label: nav.admin.user.name || nav.admin.user.email }] : []) : [];
  const crumbs = nav.admin ? adminCrumbs.slice() : [{ label: "My Courses" }];
  const base = crumbs.length;                                                     // index of the course crumb
  const devViews = nav.view === "development" || nav.view === "student-dev" || nav.view === "assignment-dev";
  if (!["courses", "admin", "admin-user"].includes(nav.view) && detail) crumbs.push({ label: detail.course.title });
  if ((nav.view === "assignment" || nav.view === "submission" || nav.view === "assignment-writing") && subs) crumbs.push({ label: subs.assignment.title });
  if (nav.view === "assignment-writing") crumbs.push({ label: "Writing for This Assignment" });
  if (nav.view === "submission" && sub) crumbs.push({ label: nav.admin?.user && !detail ? sub.assignment?.title || "Work" : sub.student?.name || "Student" });   // from Admin → a student: the work, not the name twice
  if (devViews) crumbs.push({ label: "Writing Development" });
  if (nav.view === "roster") crumbs.push({ label: "Roster" });
  if ((nav.view === "student-dev" || nav.view === "assignment-dev") && devStudent) crumbs.push({ label: devStudent.name });
  if (nav.view === "assignment-dev" && devSession) crumbs.push({ label: devSession.title });
  const onCrumb = (i) => {
    if (nav.admin) {
      if (i === 0) return openAdmin();
      if (i === 1 && nav.admin.user) return openAdminUser(nav.admin.user);
    } else if (i === 0) return setNav({ view: "courses" });
    const j = i - base;                                                            // 0 = course, 1 = assignment / development, 2 = student
    if (j === 0) return nav.courseId ? openCourse(nav.courseId) : undefined;
    if (devViews) {
      if (j === 1) return setNav({ ...nav, view: "development", courseId: nav.courseId });
      if (j === 2) return setNav({ ...nav, view: "student-dev", courseId: nav.courseId });
      return;
    }
    if (j === 1 && subs) return openAssignment(subs.assignment);
  };

  return (
    <Shell user={user} onSignOut={onSignOut} crumbs={crumbs} onCrumb={onCrumb} section={nav.admin ? "admin" : "courses"} onSection={(sec) => (sec === "admin" ? openAdmin() : setNav({ view: "courses" }))} ownsCourses={courses.length > 0}>
      {nav.view === "admin" && user.role === "admin" && (
        <AdminPanel user={user} tab={nav.admin?.tab || "teachers"} onTab={openAdmin} onOpenUser={openAdminUser} />
      )}
      {nav.view === "admin-user" && user.role === "admin" && nav.admin?.user && (
        <AdminUserDetail key={nav.admin.user.id} user={nav.admin.user} onBack={() => openAdmin()} onOpenCourse={openCourse} onOpenSubmission={openAdminSubmission} />
      )}
      {nav.view === "courses" && (
        <div data-testid="teacher-courses">
          <div className="flex flex-wrap items-baseline justify-between gap-3 mb-6"><h1 className="font-serif text-3xl text-stone-900">{user.role === "admin" ? "My courses" : "My Courses"}</h1>{user.role === "teacher" && <WalkthroughLink tour="teacher" testid="teacher-walkthrough-link" label="How the dashboard works — 2 min" />}</div>
          <div className="grid sm:grid-cols-2 gap-4">
            {courses.map((c) => (
              <button key={c.id} onClick={() => openCourse(c.id)} data-testid={`course-card-${c.id}`}
                className="text-left bg-white border border-stone-200 rounded-md p-5 hover:border-[#8C3A2A]">
                <div className="font-serif text-lg text-stone-900">{c.title}</div>
                <div className="mt-1 text-[13px] text-stone-500">{c.student_count} students · {c.assignment_count} assignments</div>
                <div className="mt-2 text-[12px] font-mono">Join code: <span className="text-[#8C3A2A]">{c.join_code}</span></div>
                <div className="mt-2 text-[12px] text-[#8C3A2A]">{c.student_count ? "Open course \u2192" : "Open course to send the code to students \u2192"}</div>
              </button>
            ))}
          </div>
          <div className="mt-8 bg-white border border-stone-200 rounded-md p-5 max-w-md">
            <div className="font-medium text-stone-800 mb-3">New course</div>
            <input data-testid="course-title" className="w-full border border-stone-300 rounded-sm px-3 py-2 text-sm mb-2"
              placeholder="Course title" value={newCourse.title} onChange={(e) => setNewCourse({ ...newCourse, title: e.target.value })} />
            <button data-testid="create-course" className={primary} disabled={!newCourse.title.trim()}
              onClick={async () => { await cCreateCourse(newCourse); setNewCourse({ title: "", description: "" }); loadCourses(); toast.success("Course created"); }}>Create course</button>
          </div>
        </div>
      )}

      {nav.view === "course" && detail && (
        <div data-testid="teacher-course-detail">
          {renaming === null ? (
            <div className="flex flex-wrap items-baseline gap-3">
              <h1 className="font-serif text-3xl text-stone-900">{detail.course.title}</h1>
              <button data-testid="rename-course" onClick={() => setRenaming(detail.course.title)} className="text-[12px] text-stone-500 hover:text-[#8C3A2A] underline underline-offset-2">Rename</button>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-2" data-testid="rename-course-form">
              <input data-testid="rename-course-input" autoFocus className="border border-stone-300 rounded-sm px-3 py-2 text-lg font-serif w-full max-w-md" value={renaming} onChange={(e) => setRenaming(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") renameCourse(); if (e.key === "Escape") setRenaming(null); }} />
              <button data-testid="rename-course-save" className={primary + " !py-1.5 !px-3 text-[13px]"} disabled={!renaming.trim()} onClick={renameCourse}>Save</button>
              <button className={ghost + " !py-1.5 !px-3 text-[13px]"} onClick={() => setRenaming(null)}>Cancel</button>
            </div>
          )}
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <p className="text-[13px] font-mono">Join code: <span data-testid="course-join-code" className="text-[#8C3A2A] text-[15px] tracking-wider">{detail.course.join_code}</span></p>
            <button data-testid="copy-join-code" className="text-[12px] text-stone-500 hover:text-[#8C3A2A] underline underline-offset-2"
              onClick={async () => { try { await navigator.clipboard.writeText(detail.course.join_code); toast.success("Join code copied"); } catch { toast.error("Could not copy"); } }}>Copy code</button>
            <button data-testid="open-invite-students" className={primary + " !py-1.5 !px-3 text-[13px]"} onClick={() => setInviteOpen((o) => !o)}>
              {inviteOpen ? "Hide invitations" : "Send code to students by email"}
            </button>
          </div>
          {inviteOpen ? <InviteStudents courseId={detail.course.id} joinCode={detail.course.join_code} /> : null}
          <p className="mt-2 text-[12.5px] text-stone-500">Students sign in (or create a student account), choose <em>My classes</em>, and enter this code — or use the link in the invitation email, which enters it for them.</p>
          <div className="mt-4 inline-flex items-center gap-1 bg-stone-100 border border-stone-200 rounded-md p-1" data-testid="course-mode-tabs">
            <span className="px-4 py-1.5 text-sm rounded-sm bg-white text-stone-900 shadow-sm font-medium" data-testid="course-mode-student-work">Student Work</span>
            <button data-testid="open-development" className="px-4 py-1.5 text-sm rounded-sm text-stone-600 hover:text-stone-900"
              onClick={() => setNav({ admin: nav.admin, view: "development", courseId: detail.course.id })}>
              Development
            </button>
            <button data-testid="open-roster" className="px-4 py-1.5 text-sm rounded-sm text-stone-600 hover:text-stone-900"
              onClick={() => setNav({ admin: nav.admin, view: "roster", courseId: detail.course.id })}>
              Roster
            </button>
            <button data-testid="open-gradebook" className="px-4 py-1.5 text-sm rounded-sm text-stone-600 hover:text-stone-900"
              onClick={() => setNav({ admin: nav.admin, view: "gradebook", courseId: detail.course.id })}>
              Assessments
            </button>
            <button data-testid="open-writing-profile" className="px-4 py-1.5 text-sm rounded-sm text-stone-600 hover:text-stone-900"
              onClick={() => setNav({ admin: nav.admin, view: "writing-profile", courseId: detail.course.id, courseTitle: detail.course.title })}>
              Writing in This Course
            </button>
          </div>
          <p className="mt-2 text-[13px] text-stone-500 max-w-2xl">
            <span className="font-medium text-stone-700">Student Work</span> — students down the rows, assignments across the
            columns. Click a cell to open that student's work. Switch to <span className="font-medium text-stone-700">Development</span> to
            see how each student's writing and thinking are changing over time.
          </p>
          <p data-testid="due-reminder-note" className="mt-1 text-[12px] text-stone-400 max-w-2xl">
            Work that arrives after a due date is marked <span className="font-mono uppercase text-[10px] text-[#8C3A2A]">Late</span>. Students with unfinished
            work are emailed a reminder two days before each due date and again on the day.
          </p>

          <div className="mt-6 bg-white border border-stone-200 rounded-lg overflow-x-auto" data-testid="work-grid">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b border-stone-200 bg-stone-50">
                  <th className="text-left px-4 py-3 sticky left-0 bg-stone-50 z-10 text-[12px] font-mono uppercase tracking-wider text-stone-400">
                    <span className="inline-flex items-center gap-2">
                      {detail.students.length ? <input type="checkbox" data-testid="roster-select-all" aria-label="Select all students" checked={detail.students.every((s) => rosterSel.has(s.id))} onChange={() => setRosterSel(detail.students.every((s) => rosterSel.has(s.id)) ? new Set() : new Set(detail.students.map((s) => s.id)))} /> : null}
                      Student
                      {rosterSel.size ? <button data-testid="roster-remove-selected" onClick={removeSelected} className="ml-2 normal-case tracking-normal font-sans text-[12px] font-medium text-[#8C3A2A] hover:underline">Remove {rosterSel.size} from course</button> : null}
                    </span>
                  </th>
                  {liveAsgs.map((a) => (
                    <th key={a.id} data-testid={`work-col-${a.id}`} className="px-3 py-3 text-center align-bottom min-w-[132px]">
                      <button data-testid={`open-assignment-${a.id}`} onClick={() => openAssignment(a)} title="Open assignment" className="text-[12px] font-medium text-stone-700 max-w-[180px] mx-auto hover:text-[#8C3A2A] underline-offset-2 hover:underline">{a.title}</button>
                      <div data-testid={`asg-due-${a.id}`} className={`mt-1 text-[11px] font-normal ${a.due_date ? (isPastDue(a.due_date) ? "text-[#8C3A2A]" : "text-stone-500") : "text-stone-300"}`}>{a.due_date ? dueLabel(a.due_date) : "No due date"}</div>
                      <div className="mt-1 flex justify-center gap-2 text-[11px] font-normal">
                        <button data-testid={`edit-assignment-${a.id}`} onClick={() => { setReassign(null); setEditAsg({ ...EMPTY_ASG, ...a, due_date: a.due_date || "", writing_path: a.writing_path || "", essay_structure: a.essay_structure || "", assignment_requirements: a.assignment_requirements || [] }); }} className="text-stone-500 hover:text-[#8C3A2A] underline underline-offset-2">Edit</button>
                        <button data-testid={`reassign-assignment-${a.id}`} onClick={() => { setEditAsg(null); setReassign({ a, courseId: detail.course.id, due_date: "" }); }} className="text-stone-500 hover:text-[#8C3A2A] underline underline-offset-2">Reassign</button>
                        <button data-testid={`archive-assignment-${a.id}`} onClick={() => archiveAsg(a, true)} title="Hide from students; all work is kept" className="text-stone-500 hover:text-[#8C3A2A] underline underline-offset-2">Archive</button>
                        <button data-testid={`delete-assignment-${a.id}`} onClick={() => { setEditAsg(null); setReassign(null); setDeleting({ a, typed: "" }); }} className="text-stone-400 hover:text-[#8C3A2A] underline underline-offset-2">Delete</button>
                      </div>
                    </th>
                  ))}
                  {!liveAsgs.length && <th className="px-4 py-3 text-[13px] text-stone-400 font-normal">{archivedAsgs.length ? "All assignments are archived — restore one below, or add a new one" : "No assignments yet — add one below"}</th>}
                </tr>
              </thead>
              <tbody>
                {detail.students.map((s) => (
                  <tr key={s.id} data-testid={`work-row-${s.id}`} className="border-b border-stone-100 last:border-0">
                    <td className="px-4 py-3 sticky left-0 bg-white z-10 whitespace-nowrap">
                      <span className="inline-flex items-center gap-2">
                        <input type="checkbox" data-testid={`roster-select-${s.id}`} aria-label={`Select ${s.name}`} checked={rosterSel.has(s.id)} onChange={() => setRosterSel((s0) => { const n = new Set(s0); if (n.has(s.id)) n.delete(s.id); else n.add(s.id); return n; })} />
                        <span className="font-serif text-lg text-stone-900">{s.name}</span>
                      </span>
                    </td>
                    {liveAsgs.map((a) => {
                      const cell = workGrid?.[`${a.id}:${s.id}`];
                      const has = !!(cell && cell.session_id);
                      return (
                        <td key={a.id} className="px-3 py-3 text-center">
                          {has ? (
                            <button data-testid={`work-cell-${s.id}-${a.id}`} onClick={() => openSub(cell.session_id)}
                              title={`Open ${s.name} · ${a.title}${cell.late ? " · submitted after the due date" : ""}`}
                              className={`group inline-flex items-center gap-2 rounded-full border px-2.5 py-1 transition-colors hover:border-[#8C3A2A] ${cell.late ? "border-[#8C3A2A]/50" : "border-stone-200"}`}>
                              <StatusPill s={cell.status} />
                              {cell.late ? <LateFlag testid={`late-flag-${s.id}-${a.id}`} /> : null}
                              <span className="text-[12px] text-stone-500 group-hover:text-stone-800">Open →</span>
                            </button>
                          ) : (
                            <span className="text-stone-300" data-testid={`work-cell-empty-${s.id}-${a.id}`}>{workGrid === null ? "·" : "—"}</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                {!detail.students.length && (
                  <tr><td colSpan={liveAsgs.length + 1} className="px-4 py-6 text-center text-[13px] text-stone-400">
                    No students enrolled yet. Share the join code <span className="font-mono text-[#8C3A2A]">{detail.course.join_code}</span>.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>

          {editAsg && (
            <div data-testid="edit-assignment-panel" className="mt-4 bg-white border border-[#8C3A2A]/40 rounded-md p-4 w-full max-w-md">
              <div className="text-[13px] font-medium text-stone-700 mb-2">Edit assignment</div>
              <AssignmentFields asg={editAsg} onChange={setEditAsg} prefix="edit-" />
              <p className="text-[11px] text-stone-400 mb-3">Changes to how students begin and what structure they use apply to students who have not started yet; students already working keep their path.</p>
              <div className="flex gap-2"><button data-testid="edit-asg-save" className={primary} disabled={!editAsg.title.trim() || !editAsg.prompt.trim()} onClick={saveAsg}>Save</button><button className={ghost} onClick={() => setEditAsg(null)}>Cancel</button></div>
            </div>
          )}
          {reassign && (
            <div data-testid="reassign-panel" className="mt-4 bg-white border border-[#8C3A2A]/40 rounded-md p-4 w-full max-w-md">
              <div className="text-[13px] font-medium text-stone-700">Reassign “{reassign.a.title}”</div>
              <p className="text-[12px] text-stone-500 mt-1">Places this assignment in the course you choose, with its own due date. Students' existing work on the original stays where it is.</p>
              <label className="block text-[12px] text-stone-600 mt-3 mb-1" htmlFor="reassign-course">To which course?</label>
              <select id="reassign-course" data-testid="reassign-course" className="w-full border border-stone-300 rounded-sm px-3 py-2 text-sm mb-2 bg-white" value={reassign.courseId} onChange={(e) => setReassign({ ...reassign, courseId: e.target.value })}>
                {courses.map((c) => <option key={c.id} value={c.id}>{c.title}{c.id === detail.course.id ? " (this course — assign again)" : ""}</option>)}
              </select>
              <label className="block text-[12px] text-stone-600 mb-1" htmlFor="reassign-due">Due date</label>
              <input id="reassign-due" data-testid="reassign-due" type="date" className="border border-stone-300 rounded-sm px-3 py-2 text-sm mb-3 bg-white" value={reassign.due_date} onChange={(e) => setReassign({ ...reassign, due_date: e.target.value })} />
              <div className="flex gap-2"><button data-testid="reassign-confirm" className={primary} onClick={doReassign}>Reassign</button><button className={ghost} onClick={() => setReassign(null)}>Cancel</button></div>
            </div>
          )}
          <details className="mt-4 bg-white border border-stone-200 rounded-md p-4 w-full max-w-md" data-testid="reuse-assignment-panel" onToggle={(e) => { if (e.currentTarget.open && library === null) loadLibrary(); }}>
            <summary className="cursor-pointer text-[13px] font-medium text-stone-700">↻ Use an assignment from another course</summary>
            <div className="mt-3">
              {library === null ? <p className="text-[12px] text-stone-400">Loading…</p> : !library.filter((x) => x.course_id !== detail.course.id).length ? <p className="text-[12px] text-stone-400">No assignments in your other courses yet.</p> : (
                <>
                  <select data-testid="reuse-source" className="w-full border border-stone-300 rounded-sm px-3 py-2 text-sm mb-2 bg-white" value={reuse.source} onChange={(e) => setReuse({ ...reuse, source: e.target.value })}>
                    <option value="">Choose an assignment…</option>
                    {library.filter((x) => x.course_id !== detail.course.id).map((x) => <option key={x.id} value={x.id}>{x.title} — {x.course_title}{x.archived ? " (archived)" : ""}</option>)}
                  </select>
                  <label className="block text-[12px] text-stone-600 mb-1" htmlFor="reuse-due">Due date in this course</label>
                  <input id="reuse-due" data-testid="reuse-due" type="date" className="border border-stone-300 rounded-sm px-3 py-2 text-sm mb-3 bg-white" value={reuse.due_date} onChange={(e) => setReuse({ ...reuse, due_date: e.target.value })} />
                  <button data-testid="reuse-confirm" className={primary} disabled={!reuse.source} onClick={async () => { try { await cCopyAssignment(detail.course.id, { source_assignment_id: reuse.source, due_date: reuse.due_date }); setReuse({ source: "", due_date: "" }); toast.success("Assignment added to this course"); await openCourse(detail.course.id); loadCourses(); } catch (e) { toast.error(e?.response?.data?.detail || "Could not add the assignment"); } }}>Add to this course</button>
                </>
              )}
            </div>
          </details>
          {deleting ? (
            <div data-testid="delete-assignment-panel" className="mt-4 bg-white border border-[#8C3A2A]/40 rounded-md p-4 w-full max-w-md">
              <p className="text-[13px] font-medium text-stone-800">Delete “{deleting.a.title}”?</p>
              <p className="mt-1 text-[12.5px] text-stone-600">This removes the assignment from your course and from every student's list. Student writing is kept in the record but will no longer be reachable from this course. If you only want to hide it, use <button onClick={() => { setDeleting(null); archiveAsg(deleting.a, true); }} className="underline underline-offset-2 text-[#8C3A2A]">Archive</button> instead.</p>
              <label className="block mt-3 text-[12px] text-stone-600" htmlFor="delete-confirm">To confirm, type the assignment title exactly</label>
              <input id="delete-confirm" data-testid="delete-confirm-input" value={deleting.typed} onChange={(e) => setDeleting({ ...deleting, typed: e.target.value })} placeholder={deleting.a.title}
                className="mt-1 w-full border border-stone-300 rounded-sm px-3 py-2 text-sm bg-white focus:outline-none focus:border-[#8C3A2A]" />
              <div className="mt-3 flex gap-2">
                <button data-testid="delete-confirm-button" onClick={deleteAsg} disabled={deleting.typed.trim() !== deleting.a.title.trim()} className={primary + " disabled:opacity-40"}>Delete permanently</button>
                <button data-testid="delete-cancel-button" onClick={() => setDeleting(null)} className={ghost}>Cancel</button>
              </div>
            </div>
          ) : null}
          {archivedAsgs.length ? (
            <details className="mt-4 bg-stone-50 border border-stone-200 rounded-md p-4 w-full max-w-md" data-testid="archived-assignments-panel">
              <summary className="cursor-pointer text-[13px] font-medium text-stone-600">Archived assignments ({archivedAsgs.length})</summary>
              <p className="mt-2 text-[12px] text-stone-500">Hidden from students. All student work is kept; restore an assignment to show it again.</p>
              <ul className="mt-3 space-y-2">
                {archivedAsgs.map((a) => (
                  <li key={a.id} data-testid={`archived-assignment-${a.id}`} className="flex items-center justify-between gap-3 bg-white border border-stone-200 rounded-sm px-3 py-2">
                    <div className="min-w-0">
                      <button data-testid={`open-archived-assignment-${a.id}`} onClick={() => openAssignment(a)} className="text-[13px] font-medium text-stone-700 hover:text-[#8C3A2A] hover:underline underline-offset-2 truncate block max-w-[260px] text-left">{a.title}</button>
                      <div className="text-[11px] text-stone-400">{a.due_date ? dueLabel(a.due_date) : "No due date"}</div>
                    </div>
                    <span className="shrink-0 flex items-center gap-3 text-[12px]">
                      <button data-testid={`restore-assignment-${a.id}`} onClick={() => archiveAsg(a, false)} className="text-[#8C3A2A] hover:underline underline-offset-2">Restore</button>
                      <button data-testid={`delete-assignment-${a.id}`} onClick={() => setDeleting({ a, typed: "" })} className="text-stone-400 hover:text-[#8C3A2A] hover:underline underline-offset-2">Delete</button>
                    </span>
                  </li>
                ))}
              </ul>
            </details>
          ) : null}
          <details className="mt-4 bg-white border border-stone-200 rounded-md p-4 w-full max-w-md" data-testid="new-assignment-panel">
            <summary className="cursor-pointer text-[13px] font-medium text-stone-700">＋ New assignment</summary>
            <div className="mt-3">
              <AssignmentFields asg={newAsg} onChange={setNewAsg} prefix="" />
              <button data-testid="create-assignment" className={primary} disabled={!newAsg.title.trim() || !newAsg.prompt.trim()}
                onClick={async () => { await cCreateAssignment(detail.course.id, newAsg); setNewAsg(EMPTY_ASG); openCourse(detail.course.id); toast.success("Assignment created"); }}>Create assignment</button>
            </div>
          </details>
        </div>
      )}

      {nav.view === "assignment" && subs && (
        <div data-testid="teacher-assignment-detail">
          <h1 className="font-serif text-3xl text-stone-900">{subs.assignment.title}{subs.assignment.archived ? <span data-testid="assignment-archived-badge" className="ml-3 align-middle text-[11px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full bg-stone-100 text-stone-500">Archived</span> : null}</h1>
          <p className="mt-2 text-[14px] text-stone-600 max-w-2xl">{subs.assignment.prompt}</p>
          <button data-testid="open-assignment-writing" className={ghost + " mt-3 inline-block"}
            onClick={() => setNav({ admin: nav.admin, view: "assignment-writing", courseId: nav.courseId, a: nav.a, assignmentId: subs.assignment.id })}>
            Writing for This Assignment →
          </button>
          <h2 className="mt-6 text-sm font-mono uppercase tracking-wider text-stone-400 mb-3">Student submissions</h2>
          <div className="space-y-2" data-testid="submission-rows">
            {subs.submissions.map((r) => (
              <div key={r.student.id} data-testid={`sub-row-${r.student.id}`}
                className="bg-white border border-stone-200 rounded-md p-4 flex items-center justify-between">
                <div><span className="text-stone-800 font-medium">{r.student.name}</span>
                  <span className="ml-3 text-[12px] text-stone-400">{r.iteration_count} iterations</span>
                  {r.level5_flags > 0 && (
                    <span data-testid={`level5-badge-${r.student.id}`} className="ml-3 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border border-[#8C3A2A]/40 bg-[#8C3A2A]/5 text-[#8C3A2A]">
                      strong scaffolding ×{r.level5_flags}
                    </span>
                  )}</div>
                <div className="flex items-center gap-3">
                  {r.late ? <LateFlag testid={`late-flag-sub-${r.student.id}`} /> : null}
                  <StatusPill s={r.status} />
                  {r.session_id && <button className={ghost} data-testid={`view-sub-${r.student.id}`} onClick={() => openSub(r.session_id)}>View</button>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {nav.view === "submission" && sub && (
        <div data-testid="teacher-submission-detail">
          <button onClick={() => (nav.admin?.user && !detail ? openAdminUser(nav.admin.user) : openCourse(nav.courseId))} data-testid="submission-back" className="text-[13px] text-stone-500 hover:text-stone-800 mb-3">← Back to student work</button>
          <h1 className="font-serif text-3xl text-stone-900">{sub.student?.name}</h1>
          <p className="text-[13px] text-stone-500 mb-6">{sub.assignment?.title}</p>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-white border border-stone-200 rounded-md p-4">
              <div className="text-[11px] font-mono uppercase tracking-wider text-stone-400 mb-1">Initial</div>
              <p data-testid="initial-text" className="text-sm text-stone-800 whitespace-pre-wrap">{sub.submission.entry_text || "—"}</p>
            </div>
            <div className="bg-white border border-stone-200 rounded-md p-4">
              <div className="text-[11px] font-mono uppercase tracking-wider text-stone-400 mb-1">Final</div>
              <p data-testid="final-text" className="text-sm text-stone-800 whitespace-pre-wrap">{sub.submission.final_text || "—"}</p>
            </div>
          </div>
          <button onClick={() => setOpenIter(!openIter)} data-testid="toggle-history" className={ghost + " mt-4"}>
            {openIter ? "Hide" : "Show"} revision history ({sub.submission.iteration_count})
          </button>
          {openIter && (
            <div className="mt-4 space-y-3" data-testid="iteration-history">
              {sub.submission.iterations.map((it) => (
                <div key={it.iteration_id} data-testid={`iteration-${it.iteration_number}`} className="bg-white border border-stone-200 rounded-md p-4">
                  <div className="flex items-center gap-3 mb-1">
                    <span className="text-[12px] font-mono text-[#8C3A2A]">#{it.iteration_number}</span>
                    <span className="text-[11px] font-mono uppercase tracking-wider bg-stone-100 text-stone-600 px-2 py-0.5 rounded-full">{it.stage}</span>
                    {it.instructional_operation && <span className="text-[11px] text-stone-400">{it.instructional_operation}</span>}
                    <span className="text-[11px] text-stone-300 ml-auto">{it.timestamp}</span>
                  </div>
                  <p className="text-sm text-stone-800 whitespace-pre-wrap">{it.text}</p>
                </div>
              ))}
            </div>
          )}
          {sub.process && <WritingPathSection p={sub.process} />}
          <AuthorshipBox authorship={sub.submission.authorship} record={sub.authorship_record} />
          <div className="mt-6 bg-white border border-stone-200 rounded-md p-4 max-w-2xl">
            <div className="text-sm font-medium text-stone-800 mb-2">Feedback to student</div>
            <textarea data-testid="feedback-input" className="w-full border border-stone-300 rounded-sm px-3 py-2 text-sm h-24"
              value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Write a comment for this student…" />
            <button data-testid="save-feedback" className={primary + " mt-2"}
              onClick={async () => { await cSaveFeedback(nav.sid, comment); toast.success("Feedback saved"); }}>Save feedback</button>
          </div>
          {sub.student?.id && sub.assignment?.id && (
            <div className="max-w-2xl">
              <TeacherAssessmentPanel courseId={nav.courseId} assignmentId={sub.assignment.id} studentId={sub.student.id} />
            </div>
          )}
          <StrongScaffoldingPanel sessionId={nav.sid} />
          <DevelopmentalRangePanel sessionId={nav.sid} />
        </div>
      )}

      {nav.view === "assignment-writing" && (
        <div data-testid="teacher-assignment-writing-view">
          <button data-testid="assignment-writing-back" onClick={() => setNav({ admin: nav.admin, view: "assignment", courseId: nav.courseId, a: nav.a })} className="text-[13px] text-stone-500 hover:text-stone-800 mb-3">← Back to assignment</button>
          <AssignmentWritingProfile assignmentId={nav.assignmentId} />
        </div>
      )}

      {nav.view === "writing-profile" && (
        <div data-testid="teacher-writing-profile-view">
          <button onClick={() => openCourse(nav.courseId)} className="text-[13px] text-stone-500 hover:text-stone-800 mb-3">← Back to course</button>
          <CourseWritingProfile courseId={nav.courseId} />
        </div>
      )}

      {nav.view === "roster" && (
        <div data-testid="teacher-roster-view">
          <button onClick={() => openCourse(nav.courseId)} className="text-[13px] text-stone-500 hover:text-stone-800 mb-3">← Back to course</button>
          <CourseRoster courseId={nav.courseId} onOpenSubmission={openSub} />
        </div>
      )}

      {nav.view === "gradebook" && (
        <div data-testid="teacher-gradebook-view">
          <button onClick={() => openCourse(nav.courseId)} className="text-[13px] text-stone-500 hover:text-stone-800 mb-3">← Back to course</button>
          <CourseGradebook courseId={nav.courseId} />
        </div>
      )}

      {nav.view === "development" && (
        <CourseDevelopment
          courseId={nav.courseId}
          report={nav.report || "composition"}
          onReportChange={(r) => setNav({ ...nav, report: r })}
          onOpenStudent={(id, name, report) => { setDevStudent({ id, name }); setNav({ admin: nav.admin, view: "student-dev", courseId: nav.courseId, report: report || "composition" }); }}
          onOpenCell={(id, name, sid, title, report) => { setDevStudent({ id, name }); setDevSession({ id: sid, title }); setNav({ admin: nav.admin, view: "assignment-dev", courseId: nav.courseId, report: report || "composition" }); }}
          onBack={() => openCourse(nav.courseId)}
        />
      )}

      {nav.view === "student-dev" && devStudent && (
        <StudentDevelopment
          key={devStudent.id}
          courseId={nav.courseId}
          studentId={devStudent.id}
          studentName={devStudent.name}
          report={nav.report || "composition"}
          onReport={(r) => setNav({ ...nav, report: r })}
          onOpenAssignment={(sid, title) => { setDevSession({ id: sid, title }); setNav({ admin: nav.admin, view: "assignment-dev", courseId: nav.courseId, report: nav.report || "composition" }); }}
          onBack={() => setNav({ admin: nav.admin, view: "development", courseId: nav.courseId, report: nav.report || "composition" })}
        />
      )}

      {nav.view === "assignment-dev" && devSession && (
        <AssignmentDevelopment
          key={devSession.id}
          report={nav.report || "composition"}
          sessionId={devSession.id}
          assignmentTitle={devSession.title}
          courseId={nav.courseId}
          studentId={devStudent?.id}
          studentName={devStudent?.name}
          onReport={(r) => setNav({ ...nav, report: r })}
          onOpenAssignment={(sid, title) => { setDevSession({ id: sid, title }); setNav({ admin: nav.admin, view: "assignment-dev", courseId: nav.courseId, report: nav.report || "composition" }); }}
          onBackToStudent={() => setNav({ admin: nav.admin, view: "student-dev", courseId: nav.courseId, report: nav.report || "composition" })}
          onBackList={() => setNav({ admin: nav.admin, view: "development", courseId: nav.courseId, report: nav.report || "composition" })}
        />
      )}
    </Shell>
  );
}

// ---------------- STUDENT ----------------
// TEACHER → STUDENTS: email the course's join code (and a one-click join link). No accounts are created here — each student signs in or
// creates a student account on the link and is enrolled by the ordinary join-code path.
function InviteStudents({ courseId, joinCode }) {
  const [raw, setRaw] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const emails = raw.split(/[\s,;]+/).map((e) => e.trim()).filter((e) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e));
  const send = async () => {
    setBusy(true);
    try { const r = await cInviteStudents(courseId, emails); setResult(r); setRaw(""); toast.success(`Invitation sent to ${r.sent.length} student${r.sent.length === 1 ? "" : "s"}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not send the invitations."); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-3 bg-white border border-stone-200 rounded-md p-4 w-full max-w-md" data-testid="invite-students-panel">
      <div className="text-[14px] font-medium text-stone-800">Send the join code to your students</div>
      <p className="mt-2 text-[12.5px] text-stone-500">Each student receives the join code <span className="font-mono text-[#8C3A2A]">{joinCode}</span> and a link that enters it for them after they sign in or create a student account.</p>
      <textarea data-testid="invite-emails" rows={4} value={raw} onChange={(e) => setRaw(e.target.value)} placeholder={"student@school.edu, another@school.edu\n(one per line, or separated by commas)"}
        className="mt-3 w-full border border-stone-300 rounded-sm px-3 py-2 text-sm" />
      <div className="mt-2 flex items-center justify-between gap-3">
        <span data-testid="invite-count" className="text-[12px] text-stone-400">{emails.length} address{emails.length === 1 ? "" : "es"}</span>
        <button data-testid="invite-send" className={primary} disabled={busy || !emails.length} onClick={send}>{busy ? "Sending\u2026" : "Send invitations"}</button>
      </div>
      {result ? <p data-testid="invite-result" className="mt-2 text-[12px] text-stone-500">Sent: {result.sent.join(", ") || "none"}{result.failed.length ? ` \u00b7 Failed: ${result.failed.join(", ")}` : ""}</p> : null}
    </div>
  );
}

function Student({ user, onSignOut }) {
  const [nav, setNav] = useState({ view: "courses" });
  const [courses, setCourses] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [showArchivedMine, setShowArchivedMine] = useState(false);
  const studentArchive = async (a, archived) => {
    if (archived && !window.confirm(`Archive “${a.title}” from your list?\n\nYour work on it is kept and your teacher still sees it. You can restore it any time.`)) return;
    try { await cStudentArchiveAssignment(a.id, archived); toast.success(archived ? "Assignment archived" : "Assignment restored"); setAssignments((prev) => prev.map((x) => x.id === a.id ? { ...x, my_archived: archived } : x)); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not update the assignment"); }
  };

  const [sub, setSub] = useState(null);
  const [code, setCode] = useState("");
  const [courseTitle, setCourseTitle] = useState("");

  const loadCourses = useCallback(async () => setCourses(await cMyCourses()), []);
  useEffect(() => { loadCourses(); }, [loadCourses]);

  const openCourse = async (c) => { setCourseTitle(c.title); setAssignments(await cCourseAssignments(c.id)); setNav({ view: "assignments", courseId: c.id }); };
  const openWriting = async (a) => {
    const res = await cStartAssignment(a.id);
    // One student environment: Full Studio. It reads the session's snapshotted writing_path and runs that pathway inside.
    localStorage.setItem("compass_student_session", JSON.stringify({ id: res.session_id, mode: "ot", classroom: true }));
    window.location.href = "/?studio=full";
  };
  const viewSubmission = async (a) => { setSub(await cMySubmission(a.id)); setNav({ view: "submission", courseId: nav.courseId, a }); };

  const crumbs = [{ label: "My Courses" }];
  if (nav.view !== "courses") crumbs.push({ label: courseTitle });
  if (nav.view === "submission" && sub) crumbs.push({ label: sub.assignment?.title });
  const onCrumb = (i) => { if (i === 0) setNav({ view: "courses" }); else if (i === 1) setNav({ view: "assignments", courseId: nav.courseId }); };

  return (
    <Shell user={user} onSignOut={onSignOut} crumbs={crumbs} onCrumb={onCrumb}>
      {nav.view === "courses" && (
        <div data-testid="student-courses">
          {user.role === "user" && (
            <div className="mb-6 bg-white border border-stone-200 rounded-md p-5 flex flex-wrap items-center justify-between gap-3" data-testid="individual-welcome">
              <div>
                <h2 className="font-serif text-xl text-stone-900">Welcome to The Writing Studio</h2>
                <p className="mt-1 text-[13px] text-stone-500 max-w-md">Try the guided writing experience on your own, or join a class below with a code from your teacher.</p>
              </div>
              <a href="?experience" data-testid="individual-experience-cta" className={primary + " whitespace-nowrap no-underline"}>Start Experience The Writing Studio →</a>
            </div>
          )}
          <div className="flex flex-wrap items-baseline justify-between gap-3 mb-6"><h1 className="font-serif text-3xl text-stone-900">My Courses</h1>{user.role === "student" && <WalkthroughLink tour="student" testid="student-walkthrough-link" label="New here? How to begin — 2 min" />}</div>
          <div className="grid sm:grid-cols-2 gap-4">
            {courses.map((c) => (
              <button key={c.id} onClick={() => openCourse(c)} data-testid={`student-course-${c.id}`}
                className="text-left bg-white border border-stone-200 rounded-md p-5 hover:border-[#8C3A2A]">
                <div className="font-serif text-lg text-stone-900">{c.title}</div>
                <div className="mt-1 text-[13px] text-stone-500">{c.assignment_count} assignments</div>
              </button>
            ))}
            {!courses.length && <p className="text-[13px] text-stone-400">You haven't joined a course yet.</p>}
          </div>
          <div className="mt-8 bg-white border border-stone-200 rounded-md p-5 max-w-md">
            <div className="font-medium text-stone-800 mb-3">Join a course</div>
            <div className="flex gap-2">
              <input data-testid="join-code" className="flex-1 border border-stone-300 rounded-sm px-3 py-2 text-sm uppercase"
                placeholder="Join code" value={code} onChange={(e) => setCode(e.target.value)} />
              <button data-testid="join-submit" className={primary} disabled={!code.trim()}
                onClick={async () => { try { await cJoinCourse(code); setCode(""); loadCourses(); toast.success("Joined course"); } catch (e) { toast.error(e?.response?.data?.detail || "Invalid code"); } }}>Join</button>
            </div>
          </div>
        </div>
      )}

      {nav.view === "assignments" && (
        <div data-testid="student-assignments">
          <h1 className="font-serif text-3xl text-stone-900 mb-6">{courseTitle}</h1>
          <div className="space-y-3">
            {assignments.filter((a) => !a.my_archived).map((a) => (
              <div key={a.id} data-testid={`student-assignment-${a.id}`} className="bg-white border border-stone-200 rounded-md p-5">
                <div className="flex items-center justify-between">
                  <div className="font-serif text-lg text-stone-900">{a.title}</div>
                  <StatusPill s={a.my_status} />
                </div>
                {a.due_date ? <p data-testid={`student-due-${a.id}`} className={`mt-1 text-[12px] font-mono uppercase tracking-wider ${isPastDue(a.due_date) && a.my_status !== "complete" ? "text-[#8C3A2A]" : "text-stone-500"}`}>{dueLabel(a.due_date)}{isPastDue(a.due_date) && a.my_status !== "complete" ? " · past due" : ""}</p> : null}
                <p className="mt-1 text-[14px] text-stone-600">{a.prompt}</p>
                <div className="mt-3 flex gap-2">
                  <button className={primary} data-testid={`open-assignment-${a.id}`} onClick={() => openWriting(a)}>
                    {a.my_status === "not_started" ? "Begin writing" : "Resume"}
                  </button>
                  {a.my_status !== "not_started" && (
                    <button className={ghost} data-testid={`view-my-sub-${a.id}`} onClick={() => viewSubmission(a)}>My submission</button>
                  )}
                  <button data-testid={`student-archive-${a.id}`} onClick={() => studentArchive(a, true)} title="Hide from my list; my work is kept" className="ml-auto text-[12px] text-stone-400 hover:text-[#8C3A2A] underline underline-offset-2">Archive</button>
                </div>
              </div>
            ))}
            {!assignments.filter((a) => !a.my_archived).length && <p className="text-[13px] text-stone-400">{assignments.length ? "Everything here is archived." : "No assignments yet."}</p>}
            {assignments.some((a) => a.my_archived) && (
              <div data-testid="student-archived-panel" className="pt-2">
                <button data-testid="student-archived-toggle" onClick={() => setShowArchivedMine((v) => !v)} className="text-[12.5px] text-stone-500 hover:text-[#8C3A2A] underline underline-offset-2">
                  {showArchivedMine ? "Hide" : "Show"} archived ({assignments.filter((a) => a.my_archived).length})
                </button>
                {showArchivedMine && assignments.filter((a) => a.my_archived).map((a) => (
                  <div key={a.id} data-testid={`student-archived-${a.id}`} className="mt-2 bg-stone-50 border border-stone-200 rounded-md px-4 py-3 flex items-center justify-between gap-3">
                    <div className="min-w-0"><div className="text-[14px] text-stone-700 truncate">{a.title}</div><StatusPill s={a.my_status} /></div>
                    <div className="flex items-center gap-3 shrink-0">
                      <button data-testid={`student-restore-${a.id}`} onClick={() => studentArchive(a, false)} className="text-[12px] text-[#8C3A2A] hover:underline underline-offset-2">Restore</button>
                      {a.my_status !== "not_started" && <button className="text-[12px] text-stone-500 hover:underline underline-offset-2" onClick={() => viewSubmission(a)}>My submission</button>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {nav.view === "submission" && sub && (
        <div data-testid="student-submission">
          <h1 className="font-serif text-3xl text-stone-900">{sub.assignment?.title}</h1>
          {sub.feedback?.comment ? (
            <div data-testid="student-feedback" className="mt-4 border-l-4 pl-4 py-2 bg-white border border-stone-200 rounded-md" style={{ borderLeftColor: BRAND }}>
              <div className="text-[11px] font-mono uppercase tracking-wider text-stone-400">Teacher feedback</div>
              <p className="text-sm text-stone-800 mt-1 whitespace-pre-wrap">{sub.feedback.comment}</p>
            </div>
          ) : <p className="mt-4 text-[13px] text-stone-400" data-testid="no-feedback">No feedback yet.</p>}
          <div className="grid md:grid-cols-2 gap-4 mt-6">
            <div className="bg-white border border-stone-200 rounded-md p-4">
              <div className="text-[11px] font-mono uppercase tracking-wider text-stone-400 mb-1">Initial</div>
              <p className="text-sm text-stone-800 whitespace-pre-wrap">{sub.submission.entry_text || "—"}</p>
            </div>
            <div className="bg-white border border-stone-200 rounded-md p-4">
              <div className="text-[11px] font-mono uppercase tracking-wider text-stone-400 mb-1">Latest</div>
              <p className="text-sm text-stone-800 whitespace-pre-wrap">{sub.submission.final_text || "—"}</p>
            </div>
          </div>
          {(sub.assignment?.id || nav.a?.id) && <MyDevelopment assignmentId={sub.assignment?.id || nav.a?.id} />}
          {(sub.assignment?.id || nav.a?.id) && <StudentInstructorAssessment assignmentId={sub.assignment?.id || nav.a?.id} />}
        </div>
      )}
    </Shell>
  );
}

// Increment 8 — the Writing-Path artifacts the submission page did not show (path, phase, thesis, argument, plan, reflection, context).
// Read-only, the student's own words; everything else (initial/final text, revision history, development, feedback) is unchanged.
const Lbl = ({ children }) => <div className="text-[11px] font-mono uppercase tracking-wider text-stone-400 mb-1">{children}</div>;
function WritingPathSection({ p }) {
  const [aside, setAside] = useState(false);
  const ctx = p.context;
  const hasCtx = ctx.requires_assigned_material || ctx.requirements.length > 0 || ctx.teacher_guidance;
  return (
    <section data-testid="writing-path-section" className="mt-6 max-w-2xl bg-white border border-stone-200 rounded-md p-4 space-y-4">
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <div className="text-sm font-medium text-stone-800">Writing path</div>
        <span data-testid="wp-route" className="text-[13px] text-stone-700">{p.route_label}</span>
        <span data-testid="wp-status" className="text-[12px] text-stone-500">{p.complete ? "Completed" : `In progress · ${p.phase_label}`}</span>
      </div>
      <div>
        <Lbl>Working thesis</Lbl>
        <p data-testid="wp-thesis" className="text-sm text-stone-800 whitespace-pre-wrap">{p.thesis || "Not yet stated."}</p>
      </div>
      <div>
        <Lbl>What the student decided the essay needed to establish</Lbl>
        {p.functions.active.length ? (
          <ol data-testid="wp-functions" className="list-decimal pl-5 space-y-1">
            {p.functions.active.map((f, i) => (
              <li key={i} className="text-sm text-stone-800">{f.text}{f.placed_in.length > 1 && <span className="text-[11px] text-stone-400"> — in {f.placed_in.join(" and ")}</span>}</li>
            ))}
          </ol>
        ) : <p className="text-sm text-stone-400">Nothing yet.</p>}
        {p.functions.set_aside.length > 0 && (
          <div className="mt-2">
            <button data-testid="wp-aside-toggle" onClick={() => setAside(!aside)} className="text-[12px] text-stone-500 hover:text-stone-800">{aside ? "Hide" : "Show"} ideas set aside ({p.functions.set_aside.length})</button>
            {aside && <ul data-testid="wp-aside" className="mt-1 list-disc pl-5 space-y-1">{p.functions.set_aside.map((t, i) => <li key={i} className="text-sm text-stone-600">{t}</li>)}</ul>}
          </div>
        )}
      </div>
      {p.plan && (
        <div>
          <Lbl>How the student organized the argument · {p.plan.structure_label}</Lbl>
          <ol data-testid="wp-plan" className="space-y-2">
            {p.plan.parts.map((part, i) => (
              <li key={i} className="text-sm">
                <div className="text-stone-800 font-medium">{part.label}</div>
                {part.region === "introduction" && <div className="text-stone-600 pl-3">Thesis</div>}
                {part.purpose && <div className="text-stone-600 pl-3">Purpose: {part.purpose}</div>}
                {part.functions.length > 0 && part.functions.join("; ") !== part.purpose && <div className="text-stone-500 pl-3 text-[13px]">{part.functions.join("; ")}</div>}
              </li>
            ))}
          </ol>
        </div>
      )}
      {p.reflection && (
        <div data-testid="wp-reflection">
          <Lbl>Student reflection</Lbl>
          {p.reflection.questions.map((q, i) => (
            <div key={i} className="mt-1">
              <div className="text-[13px] text-stone-500">{q}</div>
              <p className="text-sm text-stone-800 whitespace-pre-wrap">{p.reflection.answers[i] || "—"}</p>
            </div>
          ))}
        </div>
      )}
      {p.exchanges?.length > 0 && (
        <div data-testid="wp-exchanges">
          <Lbl>Developmental exchanges</Lbl>
          {p.exchanges.map((x, i) => (
            <div key={i} data-testid="wp-exchange" className="mt-2 border-l-2 border-stone-200 pl-3">
              <div className="text-[13px] text-stone-500">TWS asked{x.target_label ? ` about ${x.target_label}` : ""}: <span className="text-stone-700">{x.prompt}</span></div>
              <p className="text-sm text-stone-800 whitespace-pre-wrap mt-0.5">{x.student_response || "—"}</p>
              <div className="text-[12px] text-stone-400 mt-0.5">{x.revised ? "The student revised the writing afterwards." : "No revision followed."}</div>
            </div>
          ))}
        </div>
      )}
      {hasCtx && (
        <div data-testid="wp-context" className="text-[13px] text-stone-600 border-t border-stone-100 pt-3">
          <Lbl>TWS was guiding against</Lbl>
          {ctx.requires_assigned_material && <div>Assigned material first: {ctx.assigned_material_note || "yes"}</div>}
          {ctx.requirements.length > 0 && <div>Requirements: {ctx.requirements.join(" · ")}</div>}
          {ctx.teacher_guidance && <div>Your note: {ctx.teacher_guidance}</div>}
        </div>
      )}
    </section>
  );
}

// AUTHORSHIP — concise, defensible status for the teacher (never an AI percentage, never "AI detected"); the verification record opens on request.
function AuthorshipBox({ authorship, record }) {
  const [open, setOpen] = useState(false);
  if (!authorship?.status) return null;
  const tone = authorship.status === "verified" ? "text-emerald-800" : authorship.status === "no_concern" ? "text-stone-700" : "text-[#8C3A2A]";
  const rec = record || [];
  return (
    <div data-testid="authorship-box" className="mt-6 bg-white border border-stone-200 rounded-md p-4 max-w-2xl">
      <p data-testid="authorship-label" className={`text-sm font-medium ${tone}`}>{authorship.label}</p>
      <p data-testid="authorship-detail" className="text-[13px] text-stone-600 mt-1">{authorship.detail}</p>
      {rec.length > 0 && (
        <button data-testid="authorship-record-toggle" onClick={() => setOpen(!open)} className="mt-2 text-[12px] text-stone-500 hover:text-stone-800 underline underline-offset-4">{open ? "Hide" : "Show"} the verification record</button>
      )}
      {open && rec.map((r) => (
        <div key={r.event_id} data-testid="authorship-record" className="mt-3 border-t border-stone-200 pt-3 text-[13px] text-stone-700">
          <p className="text-stone-500"><span className="font-mono text-[11px] uppercase tracking-wider">Why</span> — {r.reason}</p>
          {r.tasks.map((t, i) => (
            <div key={i} className="mt-2">
              <p className="text-stone-500">Asked: <span className="text-stone-800">{t.prompt}</span></p>
              <p className="text-stone-500">Student: <span className="text-stone-800 whitespace-pre-wrap">{t.response || "—"}</span></p>
            </div>
          ))}
          <p className="mt-2">Outcome: <span className="font-medium">{r.outcome}</span>{r.basis ? <span className="text-stone-500"> — {r.basis}</span> : null}</p>
        </div>
      ))}
    </div>
  );
}


export default function ClassroomApp({ forceStudent = false }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!localStorage.getItem(AUTH_TOKEN_KEY)) { setLoading(false); return; }
    fetchMe().then(setUser).catch(() => localStorage.removeItem(AUTH_TOKEN_KEY)).finally(() => setLoading(false));
  }, []);
  const signOut = () => { localStorage.removeItem(AUTH_TOKEN_KEY); try { localStorage.removeItem("compass_student_session"); } catch (e) { /* ignore */ } setUser(null); window.location.assign("/"); };   // sign out → home page
  if (loading) return <div className="min-h-screen flex items-center justify-center text-stone-400">Loading…</div>;
  if (!user) return <Auth onAuthed={(u) => { if (u?.email_verified === false) { window.location.assign("/"); return; } setUser(u); }} />;
  const roles = user.roles || [user.role];
  if (forceStudent && roles.some((r) => r === "student" || r === "user")) return <Student user={user} onSignOut={signOut} />;   // one account, several roles
  if (user.role === "student" || user.role === "user") return <Student user={user} onSignOut={signOut} />;
  if (user.role === "teacher" || user.role === "admin") return <Teacher user={user} onSignOut={signOut} />;
  return <Student user={user} onSignOut={signOut} />;
}
